import React, { forwardRef, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { cloneDeep, isEqual } from 'lodash';
import { ButtonV2 as Button } from '@praktio/common-ui';

import SettingsMenu from '../SettingsMenu/SettingsMenu';

import './ReportSettings.scss';

interface IProps {
  noNotification?: boolean;
  notification?: IReportNotification;
  onSaveClick: (notification?: IReportNotification) => Promise<void>;
}

/**
 * Custom component for react-bootstrap Dropdown.Toggle
 */
const CustomToggle = forwardRef<
  HTMLAnchorElement,
  { onClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void }
>(({ children, onClick }, ref) => (
  <Button
    ref={ref}
    variant="outline"
    icon={{ name: 'notifications', position: 'left' }}
    onClick={(e) => {
      if (!e) return;

      e.preventDefault();
      onClick(e as any);
    }}
  >
    {children}
  </Button>
));

// NOTE: This component is currently only used for notifications settings
const ReportSettings = ({
  noNotification,
  notification: initialNotification,
  onSaveClick,
}: IProps) => {
  const [notification, setNotification] = useState(initialNotification);
  const [savedChanges, setSavedChanges] = useState(false); // TODO: Might need to forwardRef to get this working
  const [show, setShow] = useState(false);

  // ===========================================================================
  // Other
  // ===========================================================================

  const hasChanges = !isEqual(initialNotification, notification);

  // ===========================================================================
  // Handlers
  // ===========================================================================

  const handleToggle = (
    isOpen: boolean,
    e: React.SyntheticEvent<unknown>,
    metadata: DropdownToggleMetadata,
  ) => {
    if (metadata.source === 'select') return;
    setShow(isOpen);
  };

  const handleNotificationChange = (value?: string, prop?: string) => {
    if (!value || !prop) return;

    const updatedNotification = cloneDeep(notification) || {};

    updatedNotification[prop] = value;
    setNotification(updatedNotification);
    setSavedChanges(false);
  };

  const handleSaveClick = async () => {
    try {
      await onSaveClick(notification);
      setSavedChanges(true);
    } catch (e) {
      console.error(e);
    }
  };

  // ===========================================================================
  // Use Effects
  // ===========================================================================

  useEffect(() => {
    setNotification(initialNotification);
  }, [initialNotification]);

  // ===========================================================================
  // Render
  // ===========================================================================

  return (
    <Dropdown className="ReportSettings" show={show} onToggle={handleToggle}>
      <Dropdown.Toggle as={CustomToggle} id="report-settings">
        Notifications
      </Dropdown.Toggle>

      <Dropdown.Menu
        as={SettingsMenu}
        notification={notification}
        onNotificationChange={handleNotificationChange}
        hasChanges={hasChanges}
        onSaveClick={handleSaveClick}
        savedChanges={savedChanges}
        noNotification={noNotification}
      ></Dropdown.Menu>
    </Dropdown>
  );
};

export default ReportSettings;
